const MAX_RETRY = 3;

export const retry = async <
  Handler extends (...args: unknown[]) => unknown,
  Output extends ReturnType<Handler>,
>(
  /**
   * A function returning a fetch handler.
   */
  handler: Handler,

  /**
   * Maximum number of times the request should be attempted.
   */
  maxRetry = MAX_RETRY,
): Promise<Output> => {
  if (maxRetry <= 1) {
    return (await handler()) as Output;
  }

  try {
    return (await handler()) as Output;
  } catch (error) {
    const remainingAttempt = maxRetry - 1;

    console.warn(
      `\`retry\` attempts a new operation (remaining attempt(s): \`${remainingAttempt}\`, cause: \`${error}\`)`,
      error,
    );

    return await retry<Handler, Output>(handler, remainingAttempt);
  }
};

export const fetchRetry = <Handler extends () => Promise<Response>>(
  /**
   * A function returning a fetch handler.
   */
  fetchHandler: Handler,

  /**
   * Maximum number of times the request should be attempted.
   */
  maxRetry = MAX_RETRY,
) => {
  return retry(async () => {
    const res = await fetchHandler();

    if (!res.ok) {
      throw new Error(
        `\`fetchRetry\` retries \`${res.url}\` call (cause: \`${
          res.status
        }\` HTTP code${
          res.statusText ? ` with the status message \`${res.statusText}\`` : ""
        })`,
      );
    }

    return res;
  }, maxRetry);
};
